// src/features/pdf/pdf-reorder/hooks/usePdfReorder.ts
import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { PDFDocument } from 'pdf-lib';
import * as pdfjs from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { ToolStatus, OutputFile } from '../../components/ToolActionArea';
import { ViewerFile } from '../../components/PdfViewerModal';
import { reorderPdfPages, extractSinglePageForPreview, ReorderablePage } from '../../utils/pdfUtils';
import { downloadFile } from '../../utils/pdfUtils';

// Set worker source for pdf.js
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export interface DisplayPage extends ReorderablePage {
    finalIndex: number;
    thumbnailUrl: string | null; // Add thumbnail URL property
}

// Interface for files managed by PDFToolWorkspace
interface FileWithId {
    id: string;
    file: File;
}

const calculateFinalIndices = (pages: (ReorderablePage & { thumbnailUrl?: string | null })[]): DisplayPage[] => {
    let finalIndexCounter = 1;
    return pages.map(page => {
        const finalIndex = page.deleted ? 0 : finalIndexCounter++;
        return { ...page, finalIndex, thumbnailUrl: page.thumbnailUrl || null };
    });
};

// Function to generate a thumbnail for a single page
const generateThumbnail = async (pdf: pdfjs.PDFDocumentProxy, pageNumber: number): Promise<string> => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 0.3 }); // Lower scale for small thumbnails
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    if (context) {
        await page.render({ canvasContext: context, viewport: viewport }).promise;
        return canvas.toDataURL('image/jpeg', 0.8); // Use JPEG for smaller size
    }
    return '';
};


export const usePdfReorder = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [pages, setPages] = useState<DisplayPage[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [outputFile, setOutputFile] = useState<OutputFile | null>(null);
    const [error, setError] = useState<string>('');
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);

    const resetAll = useCallback(() => {
        setFiles([]);
        setPages([]);
        setStatus('idle');
        setOutputFile(null);
        setError('');
        setViewerFile(null);
    }, []);

    const handleDrop = useCallback(async (acceptedFiles: File[]) => {
        resetAll();
        const file = acceptedFiles[0];
        if (!file) return;

        const newFileWithId = { id: uuidv4(), file };
        setFiles([newFileWithId]);
        setStatus('loading'); // Show loading while parsing and generating thumbnails

        try {
            const arrayBuffer = await file.arrayBuffer().then(buffer => new Uint8Array(buffer));

            // Generate placeholders first for immediate UI feedback
            const pdfDocForCount = await PDFDocument.load(arrayBuffer);
            const pageCount = pdfDocForCount.getPageCount();
            const initialPages: DisplayPage[] = Array.from({ length: pageCount }, (_, i) => ({
                id: uuidv4(),
                originalIndex: i,
                deleted: false,
                finalIndex: i + 1,
                thumbnailUrl: null, // Placeholder
            }));
            setPages(calculateFinalIndices(initialPages));
            setStatus('idle'); // UI is ready, thumbnails will load in background

            // Now, load with pdf.js and generate thumbnails asynchronously
            const pdfjsDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
            const thumbnailPromises = [];
            for (let i = 1; i <= pdfjsDoc.numPages; i++) {
                thumbnailPromises.push(generateThumbnail(pdfjsDoc, i));
            }

            const thumbnails = await Promise.all(thumbnailPromises);

            // Update pages with generated thumbnails
            setPages(currentPages => currentPages.map((page, index) => ({
                ...page,
                thumbnailUrl: thumbnails[index]
            })));

        } catch (e) {
            console.error("Error parsing PDF or generating thumbnails:", e);
            setError(t('pdf_reorder_error_generic', { ns: 'tools' }));
            setStatus('error');
            setFiles([]); // Clear file on error
        }
    }, [resetAll, t]);

    const handleDeleteFile = useCallback((fileId: string) => {
        resetAll();
    }, [resetAll]);

    const handleReorderFiles = useCallback((reorderedFiles: FileWithId[]) => {
        setFiles(reorderedFiles);
    }, []);

    const handleReorderPages = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setPages((currentPages) => {
                const oldIndex = currentPages.findIndex(p => p.id === active.id);
                const newIndex = currentPages.findIndex(p => p.id === over.id);
                const reordered = arrayMove(currentPages, oldIndex, newIndex);
                return calculateFinalIndices(reordered);
            });
        }
    }, []);

    const handleDeletePage = useCallback((idToDelete: string) => {
        setPages(currentPages => {
            const updated = currentPages.map(p =>
                p.id === idToDelete ? { ...p, deleted: !p.deleted } : p
            );
            return calculateFinalIndices(updated);
        });
    }, []);

    const handlePreviewPage = useCallback(async (idToPreview: string) => {
        const sourceFile = files[0]?.file;
        if (!sourceFile) return;

        const pageToPreview = pages.find(p => p.id === idToPreview);
        if (!pageToPreview) return;

        try {
            const pageBytes = await extractSinglePageForPreview(sourceFile, pageToPreview.originalIndex);
            setViewerFile({
                name: `${sourceFile.name} - Page ${pageToPreview.originalIndex + 1}`,
                data: pageBytes
            });
        } catch (e) {
            console.error("Failed to extract page for preview", e);
            setError(t('pdf_reorder_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    }, [files, pages, t]);

    const handleCloseViewer = () => setViewerFile(null);

    const handleProcess = useCallback(async () => {
        const sourceFile = files[0]?.file;
        if (!sourceFile) {
            setError(t('pdf_reorder_error_no_file', { ns: 'tools' }));
            setStatus('error');
            return;
        }

        setStatus('loading');
        setError('');
        try {
            const reorderedPdfBytes = await reorderPdfPages(sourceFile, pages);
            const newFileName = `${sourceFile.name.replace(/\.pdf$/i, '')}-organized.pdf`;
            const result: OutputFile = {
                name: newFileName,
                data: reorderedPdfBytes,
                size: reorderedPdfBytes.length,
            };
            setOutputFile(result);
            setStatus('success');
        } catch (e: any) {
            console.error("Error processing PDF:", e);
            setError(t(e.message || 'pdf_reorder_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    }, [files, pages, t]);

    const handleDownload = () => {
        if (outputFile) {
            downloadFile(outputFile.data, outputFile.name);
        }
    };

    const handlePreviewOutput = () => {
        if(outputFile) {
            setViewerFile(outputFile);
        }
    };

    return {
        files,
        pages,
        status,
        outputFile,
        error,
        viewerFile,
        handleDrop,
        handleDeleteFile,
        handleReorderFiles,
        handleReorderPages,
        handleDeletePage,
        handlePreviewPage,
        handleCloseViewer,
        handleProcess,
        handleReset: resetAll,
        handleDownload,
        handlePreviewOutput,
    };
};